import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  ChevronRight,
  Search,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";

interface Collaborateur {
  id: string;
  nom: string;
  prenom: string;
  photo?: string;
  poste: string;
  derniereActivite: string;
}

const ParrainDashboard = () => {
  const [collaborateurs, setCollaborateurs] = useState<Collaborateur[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Données statiques des collaborateurs
  const collaborateursData: Collaborateur[] = [
    {
      id: "1",
      nom: "Dupont",
      prenom: "Jean",
      photo: "/images/avatar-placeholder.png",
      poste: "Chef de service",
      derniereActivite: "2024-03-20T10:30:00",
    },
    {
      id: "2",
      nom: "Martin",
      prenom: "Sophie",
      photo: "/images/avatar-placeholder.png",
      poste: "Responsable RH",
      derniereActivite: "2024-03-19T15:45:00",
    },
    {
      id: "3",
      nom: "Bernard",
      prenom: "Pierre",
      photo: "/images/avatar-placeholder.png",
      poste: "Directeur adjoint",
      derniereActivite: "2024-03-20T09:15:00",
    },
    {
      id: "4",
      nom: "Petit",
      prenom: "Marie",
      photo: "/images/avatar-placeholder.png",
      poste: "Chef de projet",
      derniereActivite: "2024-03-18T14:20:00",
    },
    {
      id: "5",
      nom: "Robert",
      prenom: "Thomas",
      photo: "/images/avatar-placeholder.png",
      poste: "Responsable qualité",
      derniereActivite: "2024-03-20T11:30:00",
    },
  ];

  useEffect(() => {
    // Simuler un délai de chargement
    const timer = setTimeout(() => {
      setCollaborateurs(collaborateursData);
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Filtrage des collaborateurs
  const filteredCollaborateurs = collaborateurs.filter((collaborateur) => {
    const searchString =
      `${collaborateur.prenom} ${collaborateur.nom} ${collaborateur.poste}`.toLowerCase();
    return searchString.includes(searchTerm.toLowerCase());
  });

  // Calcul de la pagination
  const totalPages = Math.ceil(filteredCollaborateurs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCollaborateurs = filteredCollaborateurs.slice(
    startIndex,
    endIndex
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Réinitialiser à la première page lors d'une nouvelle recherche
  };

  if (loading) {
    return (
      <div className="bg-[#fefaf6] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray">
      <div className="max-w-[1600px] mx-auto px-8">
        {/* En-tête de la page */}
        <div className="py-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Tableau de bord Parrain
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Gestion de vos collaborateurs et suivi de leur progression
              </p>
            </div>
          </div>
        </div>

        {/* Statistique des collaborateurs */}
        <div className="mb-8 bg-green-100 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Collaborateurs
              </p>
              <p className="text-3xl font-bold text-gray-900">
                {collaborateurs.length}
              </p>
            </div>
          </div>
        </div>

        {/* Liste des collaborateurs */}
        <div className="bg-white rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
              <h2 className="text-lg font-medium text-gray-900">
                Vos collaborateurs
              </h2>
              <div className="mt-4 md:mt-0 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Rechercher un collaborateur..."
                  className="block w-full md:w-96 pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm"
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </div>
            </div>

            {/* Version desktop - Tableau */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Collaborateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Poste
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dernière activité
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentCollaborateurs.map((collaborateur) => (
                    <tr key={collaborateur.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {collaborateur.photo ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover"
                                src={collaborateur.photo}
                                alt={`${collaborateur.prenom} ${collaborateur.nom}`}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-500 font-medium">
                                  {collaborateur.prenom[0]}
                                  {collaborateur.nom[0]}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {collaborateur.prenom} {collaborateur.nom}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 font-medium">
                          {collaborateur.poste}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {new Date(
                            collaborateur.derniereActivite
                          ).toLocaleDateString("fr-FR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Link
                          to={`/parrain/filleul/${collaborateur.id}`}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-orange-600 bg-orange-50 hover:bg-orange-100"
                        >
                          Consulter
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Contrôles de pagination */}
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage de{" "}
                  <span className="font-medium">{startIndex + 1}</span> à{" "}
                  <span className="font-medium">
                    {Math.min(endIndex, filteredCollaborateurs.length)}
                  </span>{" "}
                  sur{" "}
                  <span className="font-medium">
                    {filteredCollaborateurs.length}
                  </span>{" "}
                  résultats
                </p>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParrainDashboard;
